import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Button as AriaButton, Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import {
  FaBookOpen,
  FaFlask,
  FaHistory,
  FaCubes,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaUserCheck,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaSchool,
  FaShieldAlt,
  FaCrown,
  FaTachometerAlt,
  FaUser,
  FaCog,
  FaBars,
  FaServer,
  FaUsers,
  FaEdit,
  FaFileAlt,
  FaLightbulb,
  FaSun,
  FaMoon
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { ROLE_DISPLAY_NAMES, normalizeUserRole, getDefaultPage } from '../utils/rbac';
import { in3dFontStyle, TrademarkSymbol } from './In3DTypography';
import { Sheet, SheetContent } from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { SchoolCodeBlock } from './SchoolCodeBlock';
import { FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { canEditLesson, hasMinimumRole } from '../utils/rbac';
import { cn } from '@/lib/utils';

const CYAN = '#5FE0FB';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed for minimal design
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [schoolCode, setSchoolCode] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  const userRole = useMemo(() => normalizeUserRole(profile?.role), [profile?.role]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Load collapsed state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      setIsCollapsed(JSON.parse(saved));
    }
  }, []);

  // Fetch school code for School Admin and Teacher.
  // Must sit above the early return below — hooks cannot be conditional.
  useEffect(() => {
    if (!profile || (userRole !== 'school' && userRole !== 'teacher')) return;
    const schoolId = profile.school_id || profile.managed_school_id;
    if (!schoolId) return;
    getDoc(doc(db, 'schools', schoolId))
      .then((snap) => {
        if (snap.exists()) setSchoolCode(snap.data()?.schoolCode || null);
      })
      .catch(() => {});
  }, [profile, userRole]);

  // Save collapsed state
  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Don't render sidebar on these pages
  const hiddenPages = ['/login', '/partner-login', '/signup', '/forgot-password', '/onboarding', '/approval-pending', '/secretbackend', '/vrlessonplayer', '/xrlessonplayer', '/in3d-demo'];
  if (hiddenPages.some(page => location.pathname.startsWith(page)) || !user) {
    return null;
  }

  // Role checks
  const isStudent = userRole === 'student';
  const isTeacher = userRole === 'teacher';
  const isPrincipal = userRole === 'principal';
  const isSchool = userRole === 'school';
  const isAssociate = userRole === 'associate';
  const isPartner = userRole === 'partner';
  const isAdmin = userRole === 'admin';
  const isSuperadmin = userRole === 'superadmin';
  const isAdminOrSuperadmin = isAdmin || isSuperadmin;
  // School administrators should NOT have access to Create, Explore, History
  const canCreate = isTeacher || isPartner || isAdminOrSuperadmin;

  // Build navigation items based on role according to the spec:
  // Associate: Dashboard, Lessons only (refine lessons, submit for approval)
  // Student: Dashboard, Lessons, Profile
  // Teacher: Dashboard, Lessons, Create, Explore, History, Studio, Profile, Settings
  // Principal: Dashboard, Lessons, Profile, Settings
  // Admin/Superadmin: Dashboard (Content), Lessons, Create, Explore, History, Approvals, System, Profile, Settings
  const getNavItems = (): NavItem[] => {
    const items: NavItem[] = [];

    // Associate: content refinement and submissions.
    if (isAssociate) {
      items.push({ path: '/dashboard/associate', label: 'Dashboard', icon: FaTachometerAlt });
      items.push({ path: '/lessons', label: 'Lessons', icon: FaBookOpen });
      items.push({ path: '/studio/content', label: 'Content Studio', icon: FaEdit });
      items.push({ path: '/studio/n8n-lesson-builder', label: 'Lesson Builder', icon: FaFlask });
      items.push({ path: '/admin/user-lessons', label: 'Community Lessons', icon: FaBookOpen });
      return items;
    }

    // LMS Dashboards - role-based
    if (isStudent) {
      items.push({ path: '/dashboard/student', label: 'Dashboard', icon: FaTachometerAlt });
    } else if (isTeacher) {
      items.push({ path: '/dashboard/teacher', label: 'Dashboard', icon: FaTachometerAlt });
    } else if (isPrincipal) {
      items.push({ path: '/dashboard/principal', label: 'Dashboard', icon: FaTachometerAlt });
    } else if (isSchool) {
      items.push({ path: '/dashboard/school', label: 'Dashboard', icon: FaTachometerAlt });
    } else if (isPartner) {
      items.push({ path: '/dashboard/partner', label: 'Dashboard', icon: FaTachometerAlt });
    } else if (isAdminOrSuperadmin) {
      // Admin/Superadmin see content dashboard
      items.push({ path: '/studio/content', label: 'Dashboard', icon: FaTachometerAlt });
    }

    // Lessons - everyone can see
    items.push({ path: '/lessons', label: 'Lessons', icon: FaBookOpen });
    items.push({ path: '/immersive-stem', label: 'Immersive STEM', icon: FaCubes });
    items.push({ path: '/lessons/vr360', label: 'VR Tours', icon: FaCubes });

    // AI Tutor (Personalized Learning) - students only; routes to /personalized-learning
    if (isStudent) {
      items.push({ path: '/personalized-learning', label: 'AI Tutor', icon: FaLightbulb });
    }

    // Creator tools - teachers, schools, admin, superadmin
    if (canCreate) {
      items.push({ path: '/main', label: 'Create', icon: FaFlask });
      items.push({ path: '/explore', label: 'Explore', icon: FaCubes });
      items.push({ path: '/history', label: 'History', icon: FaHistory });
      items.push({ path: '/create/street-view', label: 'Street View Tours', icon: FaCubes });
      items.push({ path: '/question-paper/generate', label: 'Question Papers', icon: FaFileAlt });
    }

    if (isPartner) {
      items.push({ path: '/admin/classes', label: 'Demo Classes', icon: FaUsers });
    }

    // Teacher tools - approve students
    if (isTeacher) {
      items.push({ path: '/teacher/approvals', label: 'Student Approvals', icon: FaUserCheck });
    }

    // School administrator tools - approve teachers, manage classes
    if (isSchool) {
      items.push({ path: '/school/approvals', label: 'Teacher Approvals', icon: FaUserCheck });
      items.push({ path: '/admin/classes', label: 'Class Management', icon: FaUsers });
    }

    // Admin tools - only for admin/superadmin
    if (isAdminOrSuperadmin) {
      items.push({ path: '/studio/content', label: 'Content Studio', icon: FaEdit });
      items.push({ path: '/admin/lesson-edit-requests', label: 'Lesson Edit Requests', icon: FaEdit });
      if (isSuperadmin) {
        items.push({ path: '/admin/approvals', label: 'School & User Approvals', icon: FaUserCheck });
      }
      items.push({ path: '/admin/schools', label: 'School Management', icon: FaSchool });
      items.push({ path: '/admin/classes', label: 'Class Management', icon: FaUsers });
      items.push({ path: '/admin/school-performance', label: 'School Performance', icon: FaTachometerAlt });
      items.push({ path: '/admin/partners', label: 'Partner Applications', icon: FaUsers });
      items.push({ path: '/admin/partner-oversight', label: 'Partner Oversight', icon: FaUsers });
      items.push({ path: '/admin/logs', label: 'Production Logs', icon: FaFileAlt });
      items.push({ path: '/system-status', label: 'System', icon: FaServer });
    }

    if (isSuperadmin) {
      items.push({ path: '/studio/lesson-layout', label: 'Lesson Layout', icon: FaEdit });
      items.push({ path: '/admin/3d-assets', label: '3D Assets', icon: FaCubes });
      items.push({ path: '/admin/science-3d', label: 'Science 3D', icon: FaFlask });
    }

    // Studio tools - n8n Lesson Builder for studio roles (admin, superadmin, associate)
    if (profile && (canEditLesson(profile) || hasMinimumRole(profile, 'associate'))) {
      items.push({ path: '/studio/n8n-lesson-builder', label: 'n8n Lesson Builder', icon: FaFlask });
    }

    // Principal can also access class management, student approvals, and teacher approvals
    if (isPrincipal) {
      items.push({ path: '/teacher/approvals', label: 'Student Approvals', icon: FaUserCheck });
      items.push({ path: '/school/approvals', label: 'Teacher Approvals', icon: FaChalkboardTeacher });
      items.push({ path: '/admin/classes', label: 'Class Management', icon: FaUsers });
    }

    return items;
  };

  const navItems = getNavItems();

  // Role icon mapping
  const getRoleIcon = () => {
    switch (userRole) {
      case 'student': return FaGraduationCap;
      case 'teacher': return FaChalkboardTeacher;
      case 'principal': return FaSchool;
      case 'school': return FaSchool;
      case 'associate': return FaUser;
      case 'partner': return FaUsers;
      case 'admin': return FaShieldAlt;
      case 'superadmin': return FaCrown;
      default: return FaUser;
    }
  };

  // Role accent, tuned for the dark chrome
  const getRoleTint = () => {
    switch (userRole) {
      case 'student': return '#34D399';
      case 'teacher': return '#5FE0FB';
      case 'principal': return '#A78BFA';
      case 'school': return '#8B5CF6';
      case 'associate': return '#7DD3FC';
      case 'partner': return '#2DD4BF';
      case 'admin': return '#FBBF24';
      case 'superadmin': return '#FB7185';
      default: return '#94A3B8';
    }
  };

  const RoleIcon = getRoleIcon();
  const roleTint = getRoleTint();

  const sidebarWidth = isCollapsed ? 'var(--sidebar-width-icon)' : 'var(--sidebar-width)';

  const NavContent = ({
    forceExpanded = false,
    onNavClick,
  }: {
    forceExpanded?: boolean;
    /** When set (e.g. mobile drawer), nav items use this to navigate and close drawer instead of Link */
    onNavClick?: (path: string) => void;
  }) => {
    const expanded = forceExpanded || !isCollapsed;

    const navLinkClass = (active: boolean) =>
      cn(
        'group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors',
        active ? 'bg-white/[0.07] text-white' : 'text-white/60 hover:bg-white/[0.04] hover:text-white',
        !expanded && 'justify-center px-0'
      );

    const tooltip = (label: string) => (
      <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-lg border border-white/10 bg-[#0b1120] px-2.5 py-1.5 text-[12px] text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100">
        {label}
      </span>
    );

    const renderNavLink = (
      to: string,
      label: string,
      Icon: React.ComponentType<{ className?: string }>,
      active: boolean
    ) => {
      const inner = (
        <>
          {active && (
            <span
              className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
              style={{ backgroundColor: CYAN }}
              aria-hidden
            />
          )}
          <Icon className={cn('h-4 w-4 shrink-0', active && 'text-[#5FE0FB]')} />
          {expanded && <span className="truncate">{label}</span>}
          {!expanded && tooltip(label)}
        </>
      );

      if (onNavClick) {
        return (
          <button
            type="button"
            onClick={() => onNavClick(to)}
            className={navLinkClass(active)}
            title={!expanded ? label : undefined}
          >
            {inner}
          </button>
        );
      }
      return (
        <Link to={to} className={navLinkClass(active)} title={!expanded ? label : undefined}>
          {inner}
        </Link>
      );
    };

    return (
      <div className="flex h-full flex-col bg-[#070b16] text-white">
        <div className="flex h-[72px] shrink-0 items-center gap-2 border-b border-white/10 px-3">
          <Link
            to={getDefaultPage(userRole)}
            className={cn(
              'flex min-w-0 items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-white/[0.05]',
              expanded ? 'flex-1' : 'justify-center'
            )}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#5FE0FB]/12">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={CYAN} strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2.5 21 7.5v9L12 21.5 3 16.5v-9z" />
                <path d="M3 7.5l9 5 9-5" />
                <path d="M12 12.5v9" />
              </svg>
            </span>
            {expanded && (
              <span className="min-w-0">
                <span className="block truncate font-display font-bold leading-none" style={in3dFontStyle}>
                  <span className="text-white">In3D</span>
                  <span style={{ color: CYAN }}>.ai</span>
                  <TrademarkSymbol className="ml-0.5 inline" />
                </span>
                <span className="mt-1.5 block font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">
                  {ROLE_DISPLAY_NAMES[userRole] || userRole}
                </span>
              </span>
            )}
          </Link>
        </div>

        {(isSchool || isTeacher) && schoolCode && (
          <div className="shrink-0 border-b border-white/10 px-3 py-3">
            {expanded ? (
              <SchoolCodeBlock code={schoolCode} variant="sidebar" />
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"
                    title="School code"
                    aria-label="Show school code"
                  >
                    <FaSchool className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="right" className="min-w-[220px] rounded-xl border-white/10 bg-[#0b1120] p-3 text-white shadow-2xl">
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">School code</p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold tracking-wider">{schoolCode}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(schoolCode);
                          toast.success('School code copied');
                        } catch {}
                      }}
                    >
                      <FaCopy className="h-3 w-3" /> Copy
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {expanded && (
            <p className="mb-3 px-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">Navigation</p>
          )}
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.path ||
                (item.path !== '/lessons' && location.pathname.startsWith(item.path + '/'));
              return (
                <li key={item.path}>{renderNavLink(item.path, item.label, item.icon, isActive)}</li>
              );
            })}
          </ul>
          {isAdminOrSuperadmin && (
            <>
              <div className="my-4 h-px bg-white/10" />
              <ul className="space-y-1">
                <li>{renderNavLink('/developer', 'Settings', FaCog, location.pathname === '/developer')}</li>
              </ul>
            </>
          )}
        </nav>

        <div className="shrink-0 border-t border-white/10 p-3">
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className={cn(
              'group relative mb-1.5 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] text-white/60 transition-colors hover:bg-white/[0.04] hover:text-white',
              !expanded && 'justify-center px-0'
            )}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <FaSun className="h-4 w-4 shrink-0" /> : <FaMoon className="h-4 w-4 shrink-0" />}
            {expanded && <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
            {!expanded && tooltip(theme === 'dark' ? 'Light mode' : 'Dark mode')}
          </button>

          <MenuTrigger>
            <AriaButton
              className={cn(
                'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-white/80 transition-colors hover:bg-white/[0.05] hover:text-white',
                !expanded && 'justify-center px-0'
              )}
              aria-label="Account menu"
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10"
                style={{ backgroundColor: `${roleTint}22`, color: roleTint }}
              >
                <RoleIcon className="h-4 w-4" />
              </span>
              {expanded && (
                <span className="min-w-0 flex-1 truncate text-left">
                  <span className="flex items-center gap-1.5 truncate text-[13px] font-medium text-white">
                    {ROLE_DISPLAY_NAMES[userRole] || userRole}
                    {profile?.isGuest && (
                      <Badge variant="secondary" className="px-1.5 py-0 text-[9px] font-normal">
                        Guest
                      </Badge>
                    )}
                  </span>
                  <span className="block truncate text-[11px] text-white/45">
                    {profile?.name || profile?.displayName || 'User'}
                  </span>
                </span>
              )}
            </AriaButton>
            <Popover
              placement="right top"
              className="w-64 rounded-2xl border border-white/10 bg-[#0b1120] p-2 text-white shadow-2xl"
            >
              <Menu
                aria-label="Account actions"
                className="space-y-1 outline-none"
                onAction={(key) => {
                  if (key === 'profile') {
                    navigate('/profile');
                    return;
                  }
                  if (key === 'logout') {
                    void handleLogout();
                  }
                }}
              >
                <MenuItem
                  id="profile"
                  className={({ isFocused }) =>
                    cn(
                      'flex cursor-default items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors',
                      isFocused ? 'bg-white/[0.07] text-white' : 'text-white/85'
                    )
                  }
                >
                  <FaUser className="h-4 w-4" />
                  <span className="min-w-0">
                    <span className="block text-[14px] font-medium">Profile</span>
                    <span className="block text-[12px] text-white/45">Manage your account and settings</span>
                  </span>
                </MenuItem>
                <MenuItem
                  id="logout"
                  className={({ isFocused }) =>
                    cn(
                      'flex cursor-default items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors',
                      isFocused ? 'bg-red-500/15 text-red-300' : 'text-red-300/85'
                    )
                  }
                >
                  <FaSignOutAlt className="h-4 w-4" />
                  <span className="min-w-0">
                    <span className="block text-[14px] font-medium">Logout</span>
                    <span className="block text-[12px] text-white/45">End the current session securely</span>
                  </span>
                </MenuItem>
              </Menu>
            </Popover>
          </MenuTrigger>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile: Sheet (left) */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <button
          onClick={() => setIsMobileOpen(true)}
          className="fixed left-4 top-4 z-50 flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-[#070b16]/90 text-white shadow-xl backdrop-blur-xl transition-colors hover:bg-white/[0.08] lg:hidden"
          aria-label="Open menu"
        >
          <FaBars className="h-4 w-4" />
        </button>
        <SheetContent side="left" className="w-sidebar border-white/10 bg-[#070b16] p-0">
          <div className="flex flex-1 flex-col overflow-hidden">
            <NavContent
              forceExpanded
              onNavClick={(path) => {
                navigate(path);
                setIsMobileOpen(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop: Icon sidebar */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-white/10 transition-[width] duration-300 ease-out lg:flex"
        style={{ width: sidebarWidth }}
      >
        <NavContent />
        {/* Rail: collapse toggle */}
        <button
          onClick={toggleCollapse}
          className="absolute -right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-white/12 bg-[#0b1120] text-white/70 shadow-lg transition-colors hover:bg-white/[0.12] hover:text-white"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <FaChevronRight className="h-3 w-3" /> : <FaChevronLeft className="h-3 w-3" />}
        </button>
      </aside>

      {/* Spacer */}
      <div className="hidden lg:block shrink-0 transition-[width] duration-200" style={{ width: sidebarWidth }} />
    </>
  );
};

export default Sidebar;
